import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import Swal from "sweetalert2";
import api from "../../../../Hooks/axiosApi";
import { getCrmLoanEnquiryService, getLoanEnquiryMatchService } from "../../../../Services/CRMAdminServices/CRMTaskListService";

const formatCurrency = (val: any) => {
  if (!val && val !== 0) return "$0";
  const cleaned = String(val).replace(/[$,\s]/g, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return "$0";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);
};

const parseCurrencyValue = (val: any) => {
  if (!val && val !== 0) return null;
  const cleaned = String(val).replace(/[$,\s]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const getRepaymentType = (loanData: any) => {
  const security = loanData?.propertySecurityAvailable;
  if (security === "Secured - Asset/Equipment") {
    return "P&I";
  }
  if (security === "Unsecured - Cashflow loan") {
    return "P&I";
  }
  if (security === "Secured - Property") {
    if (loanData?.repaymentType === "Principal & Interest" || loanData?.repaymentType === "P&I") {
      return "P&I";
    }
    return "Interest Only";
  }
  return "P&I";
};

const getStatusColor = (bucket: string) => {
  switch (bucket?.toUpperCase()) {
    case "MATCH": return { bg: "#e6f4ea", text: "#1e8e3e" };
    case "POSSIBLE": return { bg: "#fef7e0", text: "#f29900" };
    case "DECLINE": return { bg: "#fce8e6", text: "#d93025" };
    default: return { bg: "#f1f3f4", text: "#5f6368" };
  }
};

const getActionBtnColor = (bucket: string) => {
  switch (bucket?.toUpperCase()) {
    case "MATCH": return "success";
    case "POSSIBLE": return "warning";
    case "DECLINE": return "light";
    default: return "secondary";
  }
};

const parseTermToMonths = (termStr: string): number => {
  if (!termStr) return 0;
  const cleanStr = termStr.trim().toLowerCase();
  const match = cleanStr.match(/^(\d+)/);
  if (!match) return 0;
  const num = parseInt(match[1], 10);
  
  if (cleanStr.includes("year") && !cleanStr.includes("(")) {
    return num * 12;
  }
  
  return num;
};

const formatTermDisplay = (termStr: string) => {
  if (!termStr) return "N/A";
  const months = parseTermToMonths(termStr);
  if (months <= 0) return termStr;
  if (months === 1) return "1 month";
  if (months < 12) return `${months} months`;
  const years = months / 12;
  if (Number.isInteger(years)) {
    return `${months} (${years} ${years === 1 ? "year" : "years"})`;
  }
  return `${months} months`;
};

const calculateLoanDetails = (principal: any, rate: any, termStr: string) => {
  const p = Number(String(principal || "").replace(/[$,\s]/g, ""));
  const rStr = String(rate || "").replace(/[%,\s]/g, "");
  const rVal = Number(rStr);
  
  if (!p || !rVal || !termStr) return { monthly: null, tcob: null };
  
  const months = parseTermToMonths(termStr);
  
  if (months <= 0) return { monthly: null, tcob: null };

  const r = (rVal / 100) / 12;
  let monthly = 0;
  let tcob = 0;
  
  if (r === 0) {
     monthly = p / months;
     tcob = 0;
  } else {
     monthly = (p * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
     tcob = (monthly * months) - p;
  }
  
  return { monthly, tcob };
};

export default function LoanEnquiryResultsPage() {
  const { leadID } = useParams<{ leadID: string }>();
  const [loanData, setLoanData] = useState<any>(null);
  const [resultsData, setResultsData] = useState<any>(null);
  const [lenders, setLenders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<string>("ALL");
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const getAllTabStyle = () => {
    const isActive = activeTab === "ALL";
    const isHovered = hoveredTab === "ALL";
    return {
      padding: "6px 18px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: "bold" as const,
      cursor: "pointer",
      userSelect: "none" as const,
      transition: "all 0.2s ease-in-out",
      border: "1px solid",
      borderColor: isActive ? "#333" : "#ddd",
      backgroundColor: isActive ? "#333" : isHovered ? "#f5f5f5" : "#fff",
      color: isActive ? "#fff" : "#333",
      boxShadow: isActive ? "0 2px 6px rgba(0,0,0,0.15)" : "none",
      transform: isHovered ? "translateY(-1px)" : "none",
    };
  };

  const getMatchTabStyle = () => {
    const isActive = activeTab === "MATCH";
    const isHovered = hoveredTab === "MATCH";
    return {
      padding: "6px 18px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: "bold" as const,
      cursor: "pointer",
      userSelect: "none" as const,
      transition: "all 0.2s ease-in-out",
      border: "1px solid transparent",
      backgroundColor: isActive ? "#1e8e3e" : isHovered ? "#d4eedb" : "#e6f4ea",
      color: isActive ? "#fff" : "#1e8e3e",
      boxShadow: isActive ? "0 2px 6px rgba(30,142,62,0.25)" : "none",
      transform: isHovered ? "translateY(-1px)" : "none",
    };
  };

  const getPossibleTabStyle = () => {
    const isActive = activeTab === "POSSIBLE";
    const isHovered = hoveredTab === "POSSIBLE";
    return {
      padding: "6px 18px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: "bold" as const,
      cursor: "pointer",
      userSelect: "none" as const,
      transition: "all 0.2s ease-in-out",
      border: "1px solid transparent",
      backgroundColor: isActive ? "#f29900" : isHovered ? "#fdedc4" : "#fef7e0",
      color: isActive ? "#fff" : "#f29900",
      boxShadow: isActive ? "0 2px 6px rgba(242,153,0,0.25)" : "none",
      transform: isHovered ? "translateY(-1px)" : "none",
    };
  };

  const getDeclineTabStyle = () => {
    const isActive = activeTab === "DECLINE";
    const isHovered = hoveredTab === "DECLINE";
    return {
      padding: "6px 18px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: "bold" as const,
      cursor: "pointer",
      userSelect: "none" as const,
      transition: "all 0.2s ease-in-out",
      border: "1px solid transparent",
      backgroundColor: isActive ? "#d93025" : isHovered ? "#fad2cf" : "#fce8e6",
      color: isActive ? "#fff" : "#d93025",
      boxShadow: isActive ? "0 2px 6px rgba(217,48,37,0.25)" : "none",
      transform: isHovered ? "translateY(-1px)" : "none",
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Fetch lenders
        const lendersRes = await api.get("/api/Lenders/Getlenders");
        setLenders(Array.isArray(lendersRes?.data) ? lendersRes.data : []);

        // 2. Fetch loan data
        if (!leadID) throw new Error("No lead ID provided");
        const loanRes = await getCrmLoanEnquiryService(leadID);
        const data = loanRes?.data || loanRes;
        setLoanData(data);

        // 3. Compute productTypeStr
        let productTypeStr = "";
        switch (data.propertySecurityAvailable) {
          case "Secured - Asset/Equipment":
            productTypeStr = "asset-secured";
            break;
          case "Unsecured - Cashflow loan":
            productTypeStr = "asset-unsecured";
            break;
          case "Secured - Property":
            productTypeStr = "property";
            break;
        }

        if (!productTypeStr) {
          throw new Error("No valid property security option found to match products. Ensure the product type is set before viewing results.");
        }

        // 4. Fetch matches
        const matchRes = await getLoanEnquiryMatchService(leadID, productTypeStr);
        setResultsData(matchRes.data);

      } catch (err: any) {
        console.error("Failed to load results", err);
        setError(err.message || "Failed to load results.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [leadID]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spinner color="primary" />
        <span style={{ marginLeft: "10px" }}>Loading matches...</span>
      </div>
    );
  }

  if (error || !loanData || !resultsData) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", flexDirection: "column" }}>
        <h3 style={{ color: "red" }}>Error</h3>
        <p>{error || "Unable to load data."}</p>
      </div>
    );
  }

  const matches = Array.isArray(resultsData?.data) ? resultsData.data : [];

  const leadRepaymentType = getRepaymentType(loanData);
  const principal = Number(String(loanData.fundingRequired || "").replace(/[$,\s]/g, ""));
  
  // Parse lead requested term in months
  const termStr = loanData.loanTerm || loanData.preferredLoanTerm || "";
  let leadMonths = parseTermToMonths(termStr);
  if (leadMonths <= 0) leadMonths = 60; // default fallback to 5 years (60 months)

  const processedMatches = matches.map((rl: any, idx: number) => {
    // 1. Parse product details from loanDetailsJson (for calculation fallback)
    let loanTermMonthsMax = 0;
    let feesAndCharges: any[] = [];
    let interestCapitalised = false;

    if (rl.loanDetailsJson) {
      try {
        const parsed = JSON.parse(rl.loanDetailsJson);
        const config = parsed?.productTypeConfig;
        
        if (rl.productType === "Property" && config?.property) {
          loanTermMonthsMax = parseInt(config.property.loanTermMonthsMax) || 0;
          feesAndCharges = config.property.feesAndCharges || [];
          interestCapitalised = config.property.interestCapitalised === "Yes" || 
                                (Array.isArray(config.property.repaymentType) && 
                                 config.property.repaymentType.includes("Interest Capitalised"));
        } else if (rl.productType === "Asset - Secured" && config?.assetSecured) {
          loanTermMonthsMax = parseInt(config.assetSecured.loanTermMonthsMax) || 0;
          feesAndCharges = config.assetSecured.feesAndCharges || [];
        } else if (rl.productType === "Asset - Unsecured" && config?.assetUnsecured) {
          loanTermMonthsMax = parseInt(config.assetUnsecured.loanTermMonthsMax) || 0;
          feesAndCharges = config.assetUnsecured.feesAndCharges || [];
        }
      } catch (e) {
        console.error("Failed to parse loanDetailsJson", e);
      }
    }

    // Calculate total fees (for fallback)
    let calcFees = 0;
    if (Array.isArray(feesAndCharges)) {
      feesAndCharges.forEach((fee: any) => {
        const amount = Number(String(fee.amount || "").replace(/[$,\s]/g, ""));
        const percent = Number(String(fee.percent || "").replace(/[%,\s]/g, ""));
        if (amount) {
          calcFees += amount;
        } else if (percent) {
          calcFees += (principal * percent) / 100;
        }
      });
    }

    const isCapitalised = interestCapitalised;
    const rate = Number(rl.interestRate) || 0;

    let calcMonthly = 0;
    let calcTcob = 0;
    let termMonthsForCalc = leadMonths;

    if (isCapitalised) {
      calcMonthly = 0;
      const backendTcob = parseCurrencyValue(rl.tcob);
      if (backendTcob !== null && backendTcob > 0) {
        calcTcob = backendTcob;
      } else {
        const totalInterest = principal * (rate / 100) * (leadMonths / 12);
        calcTcob = totalInterest + calcFees;
      }
    } else if (leadRepaymentType === "P&I") {
      termMonthsForCalc = loanTermMonthsMax || leadMonths || 60;
      
      const r = (rate / 100) / 12;
      if (r === 0) {
        calcMonthly = principal / termMonthsForCalc;
      } else {
        calcMonthly = (principal * r * Math.pow(1 + r, termMonthsForCalc)) / (Math.pow(1 + r, termMonthsForCalc) - 1);
      }
      
      const totalInterest = (calcMonthly * termMonthsForCalc) - principal;
      calcTcob = totalInterest + calcFees;
    } else {
      termMonthsForCalc = leadMonths || 60;
      calcMonthly = principal * (rate / 100) / 12;
      const totalInterest = calcMonthly * termMonthsForCalc;
      calcTcob = totalInterest + calcFees;
    }

    // Use direct values from API if present, otherwise fall back to calculation
    let finalMonthly = parseCurrencyValue(rl.monthlyRepayment);
    if (finalMonthly === null || finalMonthly === undefined) {
      finalMonthly = isCapitalised ? null : calcMonthly;
    }

    let productFees = parseCurrencyValue(rl.fees);
    if (productFees === null || productFees === undefined) {
      productFees = calcFees;
    }

    let finalTcob = parseCurrencyValue(rl.tcob);
    if (finalTcob === null || finalTcob === undefined) {
      finalTcob = calcTcob;
    }

    let totalRepaid = parseCurrencyValue(rl.totalRepaid);
    if (totalRepaid === null || totalRepaid === undefined) {
      totalRepaid = principal + finalTcob;
    }

    // Rank from API rankNo or originalRank (index + 1)
    const originalRank = rl.rankNo || rl.originalRank || (idx + 1);

    return {
      ...rl,
      finalMonthly: isCapitalised ? null : finalMonthly,
      productFees,
      totalRepaid,
      finalTcob,
      isCapitalised,
      originalRank
    };
  });

  const possibleCount = processedMatches.filter((m: any) => m.matchBucket === "POSSIBLE").length;

  const filteredMatches = processedMatches.filter((m: any) => {
    if (activeTab === "ALL") return true;
    return m.matchBucket?.toUpperCase() === activeTab;
  });

  const validTcobs = processedMatches
    .filter((m: any) => m.matchBucket !== "DECLINE")
    .map((m: any) => m.finalTcob)
    .filter((n: any) => n !== null && n > 0) as number[];

  const bestTcob = validTcobs.length > 0 ? Math.min(...validTcobs) : null;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f4f6f9", paddingBottom: "40px" }}>
      <div style={{ width: "100%", backgroundColor: "#0000ff", color: "white", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: "bold", color: "#ffd700", fontSize: "28px", letterSpacing: "1px", textTransform: "uppercase" }}>FINTRACK</h2>
          <div style={{ fontSize: "16px", marginTop: "4px" }}>Results Page</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <span style={{ backgroundColor: "#ffd700", color: "#000", padding: "8px 24px", borderRadius: "25px", fontWeight: "bold", fontSize: "15px" }}>
            Lead ID {leadID} . Borrower
          </span>
          <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => window.close()}></button>
        </div>
      </div>

      <div style={{ padding: "0", margin: "20px 40px", backgroundColor: "#fff", borderRadius: "5px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
        {matches.length === 0 ? (
          <div style={{ padding: "0", textAlign: "center", backgroundColor: "#fff", borderRadius: "5px", overflow: "hidden" }}>
             <div style={{ backgroundColor: "#0000ff", color: "white", padding: "15px 40px", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
               <div>
                 <h3 style={{ margin: 0, fontSize: "18px", color: "#ffd700", fontWeight: "bold" }}>Zero Matches . No Result State</h3>
                 <div style={{fontSize:"13px", marginTop: "2px"}}>Internal staff tool - borrower never sees this</div>
               </div>
               <div style={{ color: "#ffd700", fontWeight: "bold", fontSize: "20px" }}>FINTRACK</div>
             </div>
             
             <div style={{ padding: "60px 20px", borderBottom: "5px solid #0000ff", minHeight: "500px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
               <div style={{ width: "100px", height: "100px", borderRadius: "50%", backgroundColor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                 <span style={{ fontSize: "50px", color: "#0000ff", fontWeight: "bold" }}>?</span>
               </div>
               <h2 style={{ fontWeight: "bold", color: "#333", marginBottom: "15px", fontSize: "28px" }}>No Result</h2>
               <p style={{ color: "#555", marginBottom: "10px", fontSize: "16px" }}>No lender on the panel matches this borrower's profile against current rules.</p>
               <p style={{ color: "#888", fontStyle: "italic", fontSize: "15px" }}>Staff next step: review the borrower's answers, broaden criteria, or contact a lender off-panel.</p>
             </div>
             
             <div style={{ backgroundColor: "#eef2ff", padding: "20px", textAlign: "left", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <h5 style={{ color: "#0000ff", fontSize: "15px", fontWeight: "bold", marginBottom: "8px" }}>Why no client-facing empty state?</h5>
                <p style={{ fontSize: "14px", color: "#555", margin: 0 }}>This is an internal staff tool. The borrower never sees "No Result" - staff intervene and gather more information before re-running the match.</p>
             </div>
             <div style={{ backgroundColor: "#0a0b29", color: "#ffd700", padding: "12px 40px", textAlign: "left", fontSize: "13px", fontWeight: "bold" }}>
               Empty state = staff workflow trigger
             </div>
          </div>
        ) : (
          <div style={{ padding: "0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", backgroundColor: "#f8f9fc", padding: "20px 40px", borderBottom: "1px solid #ddd", borderTopLeftRadius: "5px", borderTopRightRadius: "5px" }}>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Purpose</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{loanData.primaryPurpose || "N/A"}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Loan Amount</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{formatCurrency(loanData.fundingRequired)}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>LVR</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{loanData.lvr ? (String(loanData.lvr).endsWith("%") ? loanData.lvr : `${loanData.lvr}%`) : "N/A"}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Term</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{formatTermDisplay(loanData.loanTerm || loanData.preferredLoanTerm || "")}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Repayment Type</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{getRepaymentType(loanData)}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Credit</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{loanData.creditProfile || "N/A"}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: "#888", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "5px" }}>Security</div>
                <div style={{ fontWeight: "bold", fontSize: "16px", color: "#333" }}>{loanData.propertySecurityAvailable || "N/A"}</div>
              </div>
            </div>

            <div style={{ padding: "20px 40px" }}>
              <div style={{ display: "flex", gap: "12px", marginBottom: "20px", alignItems: "center" }}>
                <span style={{ fontSize: "13px", color: "#666", marginRight: "10px" }}>Sort: TCOB asc, then lender alphabetical</span>
                <span 
                  style={getAllTabStyle()}
                  onClick={() => setActiveTab("ALL")}
                  onMouseEnter={() => setHoveredTab("ALL")}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  All {processedMatches.length}
                </span>
                <span 
                  style={getMatchTabStyle()}
                  onClick={() => setActiveTab("MATCH")}
                  onMouseEnter={() => setHoveredTab("MATCH")}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  Match {processedMatches.filter((m: any) => m.matchBucket === "MATCH").length}
                </span>
                {possibleCount > 0 && (
                  <span 
                    style={getPossibleTabStyle()}
                    onClick={() => setActiveTab("POSSIBLE")}
                    onMouseEnter={() => setHoveredTab("POSSIBLE")}
                    onMouseLeave={() => setHoveredTab(null)}
                  >
                    Possible {possibleCount}
                  </span>
                )}
                <span 
                  style={getDeclineTabStyle()}
                  onClick={() => setActiveTab("DECLINE")}
                  onMouseEnter={() => setHoveredTab("DECLINE")}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  Decline {processedMatches.filter((m: any) => m.matchBucket === "DECLINE").length}
                </span>
              </div>

              <div style={{ backgroundColor: "white", borderRadius: "5px", overflow: "hidden", border: "1px solid #eee", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                <table className="table mb-0" style={{ fontSize: "14px", verticalAlign: "middle" }}>
                  <thead style={{ backgroundColor: "white" }}>
                    <tr>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>RANK</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>STATUS</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>LENDER</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>RATE</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>MONTHLY</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>FEES</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>LOAN TERM</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>TOTAL REPAID</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>TCOB</th>
                      <th style={{ padding: "15px", borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>VS BEST</th>
                      <th style={{ padding: "15px", paddingLeft: "55px",  borderBottom: "2px solid #eee", color: "#888", fontSize: "13px", letterSpacing: "1px" }}>ACTION</th>
                    </tr>
                  </thead>  
                  <tbody>
                    {filteredMatches.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ textAlign: "center", padding: "40px", color: "#888", fontSize: "16px" }}>
                          No results found in this category.
                        </td>
                      </tr>
                    ) : (
                      filteredMatches.map((rl: any, idx: number) => {
                        const statusColor = getStatusColor(rl.matchBucket);
                        const btnColor = getActionBtnColor(rl.matchBucket);
                        let rowLeftBorderColor = "#ddd";
                        if (rl.matchBucket === "MATCH") rowLeftBorderColor = "#1e8e3e";
                        else if (rl.matchBucket === "POSSIBLE") rowLeftBorderColor = "#f29900";
                        else if (rl.matchBucket === "DECLINE") rowLeftBorderColor = "#d93025";

                        const tcobVal = rl.finalTcob;
                        let vsBestStr = "-";
                        let vsBestColor = "#d93025";
                        if (rl.matchBucket === "DECLINE") {
                          vsBestStr = rl.resultReason || "Decline";
                          vsBestColor = "#d93025";
                        } else if (tcobVal !== null && tcobVal > 0 && bestTcob !== null) {
                          if (tcobVal <= bestTcob) {
                            vsBestStr = "Best";
                            vsBestColor = "#1e8e3e";
                          } else {
                            const diff = tcobVal - bestTcob;
                            vsBestStr = "+" + formatCurrency(diff);
                          }
                        }

                        const mappedLender = lenders.find((l: any) => String(l.lenderId) === String(rl.lenderId));
                        const finalLenderName = rl.lenderName || mappedLender?.lenderName || (rl.lenderId ? `Lender ${rl.lenderId}` : "Lender Name");

                        return (
                          <tr key={idx} style={{ backgroundColor: "white", borderBottom: "1px solid #eee" }}>
                            <td style={{ padding: "15px", borderLeft: `6px solid ${rowLeftBorderColor}`, fontWeight: "bold", fontSize: "17px", color: "#333", width: "80px", textAlign: "center" }}>
                              #{rl.originalRank}
                            </td>
                            <td style={{ padding: "15px" }}>
                              <span style={{ backgroundColor: statusColor.bg, color: statusColor.text, padding: "5px 15px", borderRadius: "15px", fontSize: "13px", fontWeight: "bold", display: "inline-block", textTransform: "uppercase" }}>
                                {rl.matchBucket || "N/A"}
                              </span>
                            </td>
                            <td style={{ padding: "15px" }}>
                              <div style={{ fontWeight: "bold", color: "#333", fontSize: "16px", marginBottom: "3px" }}>
                                {finalLenderName}
                              </div>
                              <div style={{ fontSize: "14px", color: "#666" }}>
                                {rl.productName}
                              </div>
                            </td>
                            <td style={{ padding: "15px", fontWeight: "bold", color: "#333", fontSize: "16px" }}>
                              {rl.interestRate ? `${Number(rl.interestRate).toFixed(2)}%` : "-"}
                            </td>
                            <td style={{ padding: "15px", color: "#333", fontSize: "16px" }}>
                              {rl.finalMonthly ? formatCurrency(rl.finalMonthly) : "-"}
                            </td>
                            <td style={{ padding: "15px", color: "#333", fontSize: "16px" }}>
                              {formatCurrency(rl.productFees)}
                            </td>
                            <td style={{ padding: "5px", color: "#333", fontSize: "16px" }}>
                              {formatTermDisplay(rl.loanTerm ? String(rl.loanTerm) : "")}
                            </td>
                            <td style={{ padding: "15px", color: "#333", fontSize: "16px" }}>
                              {formatCurrency(rl.totalRepaid)}
                            </td>
                            <td style={{ padding: "15px", fontWeight: "bold", color: "#0000ff", fontSize: "16px" }}>
                              {rl.finalTcob ? formatCurrency(rl.finalTcob) : "-"}
                            </td>
                            <td 
                              title={vsBestStr}
                              style={{ 
                                padding: "15px", 
                                fontWeight: rl.matchBucket === "DECLINE" ? "normal" : "bold", 
                                color: vsBestColor, 
                                fontSize: rl.matchBucket === "DECLINE" ? "13px" : "16px",
                                maxWidth: "180px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                              }}
                            >
                              {vsBestStr}
                            </td>
                            <td style={{ padding: "15px", textAlign: "right" }}>
                              {rl.matchBucket === "DECLINE" ? (
                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                  <Button 
                                    color="danger"
                                    size="sm" 
                                    style={{ fontWeight: "bold", border: "none", padding: "6px 12px", color: "white", borderRadius: "4px", fontSize: "14px", width: "110px" }}
                                    onClick={() => {
                                      Swal.fire({
                                        title: 'Decline Reason',
                                        html: `<strong>${rl.productName}</strong><br/><br/>${rl.resultReason || "No specific reason provided."}`,
                                        icon: 'info',
                                        confirmButtonColor: '#d93025'
                                      });
                                    }} 
                                  >
                                    See why
                                  </Button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                                  <Button 
                                    color="link"
                                    size="sm" 
                                    style={{ border: "1px solid #0000ff", color: "#0000ff", backgroundColor: "transparent", fontWeight: "bold", borderRadius: "4px", padding: "6px 12px", fontSize: "14px", textDecoration: "none", width: "110px", textAlign: "center" }}
                                    onClick={() => window.open(`${process.env.PUBLIC_URL}/EditLoanProduct/${rl.loanProductId}`, '_blank')}
                                  >
                                    View Loan
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ backgroundColor: "#eef2ff", borderLeft: "4px solid #0000ff", padding: "15px 20px", marginTop: "20px", borderRadius: "4px" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#0000ff", lineHeight: "1.6" }}>
                  <strong>Top 20 results.</strong> Pure pass/fail — <span style={{ color: "#1e8e3e", fontWeight: "bold" }}>green Match</span> or <span style={{ color: "#d93025", fontWeight: "bold" }}>red Decline</span>, no in-between. <strong>TCOB</strong> = interest + all fees. <strong>Total Repaid</strong> = Loan + TCOB. Repayment type is shown once in the summary (the borrower's choice, same for every product). The <strong>Savings</strong> column shows "Best" for the cheapest match and how much more each other option costs; for declines it shows the reason.
                </p>
              </div>

            </div>
          </div>
        )}
      </div>
    </div>
  );
}
